import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast, ToastContainer } from './Toast';
import axiosClient from '../services/axiosClient';
import './ModernOffersList.css';

// Fonction utilitaire pour lire le token du localStorage
function getAuthHeader() {
  try {
    // Essayer d'abord la structure principale du localStorage
    const authData = localStorage.getItem('authData');
    if (authData) {
      const data = JSON.parse(authData);
      if (data?.token && data?.tokenType) {
        return { Authorization: `${data.tokenType} ${data.token}` };
      }
    }

    // Fallback sur d'autres clés possibles
    const storages = [window.localStorage, window.sessionStorage];
    const keys = ['auth','userData','senoffre_auth','loginData','currentUser','user','userDetails'];
    for (const storage of storages) {
      for (const key of keys) {
        const raw = storage.getItem(key);
        if (!raw) continue;
        const data = JSON.parse(raw);
        if (data?.token) return { Authorization: `${data.tokenType || 'Bearer'} ${data.token}` };
      }
    }
    return {};
  } catch (error) {
    console.error('Erreur lors de la récupération du token:', error);
    return {};
  }
}

// Composant pour afficher une offre individuelle moderne
function ModernOfferCard({ offer, onSave, onApply, isSaved = false }) {
  const handleSave = () => {
    if (onSave) onSave(offer);
  };

  const handleApply = () => {
    if (onApply) onApply(offer);
  };

  return (
    <div className="modern-offer-card">
      <div className="offer-card-content">
        {/* Header avec logo et badges */}
        <div className="offer-card-header">
          <div className="company-avatar">
            {offer.logoUrl && !offer.logoUrl.includes('default/picture.jpg') ? (
              <img 
                src={offer.logoUrl} 
                alt={offer.org}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className="avatar-fallback" style={{ display: offer.logoUrl && !offer.logoUrl.includes('default/picture.jpg') ? 'none' : 'flex' }}>
              {offer.org?.charAt(0) || 'C'}
            </div>
            <div className="avatar-glow"></div>
          </div>
          
          <div className="offer-badges">
            {offer.isUrgent && <span className="offer-badge urgent">Urgent</span>}
            {offer.isFeatured && <span className="offer-badge featured">Vedette</span>}
            <span className="offer-badge active">Actif</span>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="offer-card-body">
          <div className="offer-meta">
            <h3 className="offer-title">{offer.title}</h3>
            <p className="offer-company">{offer.org}</p>
            
            <div className="offer-details">
              <div className="detail-item">
                <i className="fa fa-map-marker-alt"></i>
                <span>{offer.location}</span>
              </div>
              <div className="detail-item">
                <i className="fa fa-briefcase"></i>
                <span>{offer.type}</span>
              </div>
              <div className="detail-item">
                <i className="fa fa-clock"></i>
                <span>{offer.date}</span>
              </div>
            </div>

            <div className="offer-tags">
              <span className="offer-tag">{offer.category}</span>
              {offer.salary && <span className="offer-tag salary">{offer.salary}</span>}
            </div>

            {offer.excerpt && (
              <p className="offer-excerpt">{offer.excerpt}</p>
            )}
          </div>

          <div className="offer-stats">
            <div className="stat-item">
              <i className="fa fa-eye"></i>
              <span>{offer.views || Math.floor(Math.random() * 100)} vues</span>
            </div>
            <div className="stat-item">
              <i className="fa fa-users"></i>
              <span>{offer.applications} candidatures</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="offer-card-actions">
          <button 
            className={`action-btn save-btn ${isSaved ? 'saved' : ''}`}
            onClick={handleSave}
          >
            <i className={`fa ${isSaved ? 'fa-heart' : 'fa-heart-o'}`}></i>
            <span className="btn-text">{isSaved ? 'Retirer des favoris' : 'Sauvegarder'}</span>
          </button>
          
          <button className="action-btn apply-btn" onClick={handleApply}>
            <i className="fa fa-paper-plane"></i>
            <span className="btn-text">Postuler</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// Composant principal pour la liste des offres moderne
export default function ModernOffersList({ 
  title = "Parcourir les Offres", 
  subtitle = "Découvrez tous les Appels d'offres disponibles",
  showFilters = true,
  showPagination = true,
  maxItems = 12,
  apiEndpoint = '/posts',
  detailRouteBase = '/job-detail'
}) {
  const navigate = useNavigate();
  const { toasts, showToast, removeToast } = useToast();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOffers, setTotalOffers] = useState(0);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'list'
  const [sortBy, setSortBy] = useState('recent');
  const [filters, setFilters] = useState({
    type: '',
    location: '',
    sector: '',
    salary: '',
    experience: ''
  });
  
  // États pour les données dynamiques des APIs
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  
  // État pour les postes sauvegardés
  const [savedPosts, setSavedPosts] = useState([]);
  const [loadingSavedPosts, setLoadingSavedPosts] = useState(false);

  // Fonction pour récupérer les villes du Sénégal
  const fetchCities = async () => {
    setLoadingCities(true);
    try {
      const response = await axiosClient.get('/countries/SN/cities?perPage=100', {
        headers: {
          'Accept': 'application/json',
          ...getAuthHeader(),
        },
      });
      
      if (response.status >= 200 && response.status < 300) {
        const data = response.data;
        let cityList = data?.result?.data || data?.data || data?.result || data || [];
        if (!Array.isArray(cityList)) {
          if (cityList && Array.isArray(cityList.data)) cityList = cityList.data;
          else cityList = [];
        }
        
        // Normaliser les données des villes selon la structure API réelle
        const normalizedCities = cityList.map(city => ({
          id: city.id,
          name: city.name,
          country_code: city.country_code,
          latitude: city.latitude,
          longitude: city.longitude,
          population: city.population,
          posts_count: city.posts_count || 0
        }));
        
        setCities(normalizedCities);
      }
    } catch (e) {
      console.error('[Cities API] fetch error:', e);
      // Fallback avec villes par défaut selon la structure API
      setCities([
        { id: 1, name: 'Dakar', country_code: 'SN', latitude: 14.69, longitude: -17.44, population: 1030594, posts_count: 0 },
        { id: 2, name: 'Thiès', country_code: 'SN', latitude: 14.79, longitude: -16.93, population: 320000, posts_count: 0 },
        { id: 3, name: 'Saint-Louis', country_code: 'SN', latitude: 16.02, longitude: -16.48, population: 176000, posts_count: 0 },
        { id: 4, name: 'Kaolack', country_code: 'SN', latitude: 14.15, longitude: -16.07, population: 233708, posts_count: 0 },
        { id: 5, name: 'Ziguinchor', country_code: 'SN', latitude: 12.58, longitude: -16.27, population: 230000, posts_count: 0 },
        { id: 6, name: 'Tambacounda', country_code: 'SN', latitude: 13.77, longitude: -13.67, population: 78800, posts_count: 0 }
      ]);
    } finally {
      setLoadingCities(false);
    }
  };

  // Fonction pour récupérer les postes sauvegardés
  const fetchSavedPosts = async () => {
    setLoadingSavedPosts(true);
    try {
      const authHeaders = getAuthHeader();
      
      if (!authHeaders.Authorization) {
        // Si pas de token, pas de postes sauvegardés
        setSavedPosts([]);
        return;
      }

      const response = await axiosClient.get('/savedPosts', {
        params: {
          country_code: 'SN',
          embed: 'post,user',
          sort: 'created_at',
          perPage: 10,
        },
        headers: {
          'Accept': 'application/json',
          ...authHeaders,
        },
      });
      
      if (response.status >= 200 && response.status < 300) {
        const data = response.data;
        let savedList = data?.result?.data || data?.data || data?.result || data || [];
        if (!Array.isArray(savedList)) {
          if (savedList && Array.isArray(savedList.data)) savedList = savedList.data;
          else savedList = [];
        }
        
        // Extraire les IDs des postes sauvegardés
        const savedPostIds = savedList.map(item => {
          // L'API peut retourner soit post_id directement, soit un objet post avec id
          return item.post_id || item.post?.id || item.id;
        }).filter(Boolean);
        
        setSavedPosts(savedPostIds);
      }
    } catch (e) {
      console.error('[SavedPosts API] fetch error:', e);
      setSavedPosts([]);
    } finally {
      setLoadingSavedPosts(false);
    }
  };

  // Fonction pour récupérer les catégories
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await axiosClient.get('/categories', {
        headers: {
          'Accept': 'application/json',
          ...getAuthHeader(),
        },
      });
      
      if (response.status >= 200 && response.status < 300) {
        const data = response.data;
        let categoryList = data?.result?.data || data?.data || data?.result || data || [];
        if (!Array.isArray(categoryList)) {
          if (categoryList && Array.isArray(categoryList.data)) categoryList = categoryList.data;
          else categoryList = [];
        }
        
        // Normaliser les données des catégories selon la structure API réelle
        const normalizedCategories = categoryList.map(category => ({
          id: category.id,
          parent_id: category.parent_id,
          name: category.name,
          slug: category.slug,
          description: category.description,
          picture: category.picture,
          icon_class: category.icon_class,
          picture_url: category.picture_url,
          active: category.active,
          depth: category.depth
        }));
        
        setCategories(normalizedCategories);
      }
    } catch (e) {
      console.error('[Categories API] fetch error:', e);
      // Fallback avec catégories par défaut selon la structure API
      setCategories([
        { id: 1, parent_id: null, name: 'Informatique & Technologie', slug: 'informatique-technologie', description: '', picture: 'app/default/categories/fa-folder-default.png', icon_class: 'fa-regular fa-laptop', picture_url: '', active: 1, depth: 0 },
        { id: 2, parent_id: null, name: 'Services financiers', slug: 'financial-services', description: '', picture: 'app/default/categories/fa-folder-default.png', icon_class: 'fa-regular fa-folder', picture_url: '', active: 1, depth: 0 },
        { id: 3, parent_id: null, name: 'Marketing & Communication', slug: 'marketing-communication', description: '', picture: 'app/default/categories/fa-folder-default.png', icon_class: 'fa-regular fa-bullhorn', picture_url: '', active: 1, depth: 0 },
        { id: 4, parent_id: null, name: 'Santé & Médical', slug: 'sante-medical', description: '', picture: 'app/default/categories/fa-folder-default.png', icon_class: 'fa-regular fa-heart', picture_url: '', active: 1, depth: 0 },
        { id: 5, parent_id: null, name: 'Éducation & Formation', slug: 'education-formation', description: '', picture: 'app/default/categories/fa-folder-default.png', icon_class: 'fa-regular fa-graduation-cap', picture_url: '', active: 1, depth: 0 },
        { id: 6, parent_id: null, name: 'Commerce & Vente', slug: 'commerce-vente', description: '', picture: 'app/default/categories/fa-folder-default.png', icon_class: 'fa-regular fa-shopping-cart', picture_url: '', active: 1, depth: 0 },
        { id: 7, parent_id: null, name: 'Ingénierie & BTP', slug: 'ingenierie-btp', description: '', picture: 'app/default/categories/fa-folder-default.png', icon_class: 'fa-regular fa-wrench', picture_url: '', active: 1, depth: 0 },
        { id: 8, parent_id: null, name: 'Agriculture & Agroalimentaire', slug: 'agriculture-agroalimentaire', description: '', picture: 'app/default/categories/fa-folder-default.png', icon_class: 'fa-regular fa-leaf', picture_url: '', active: 1, depth: 0 }
      ]);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Charger les données initiales
  useEffect(() => {
    fetchCities();
    fetchCategories();
    fetchSavedPosts();
  }, []);

  useEffect(() => {
    let mounted = true;
    async function fetchOffers() {
      try {
        setLoading(true);
        setError('');
        
        const response = await axiosClient.get('/posts', {
          params: {
            page: currentPage,
            perPage: maxItems,
          },
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            ...getAuthHeader(),
          },
        });
        
        const data = response.data;
        let items = data?.result?.data || data?.data || data?.result || data || [];
        
        if (!Array.isArray(items)) {
          if (items && Array.isArray(items.data)) items = items.data;
          else items = [];
        }

        // Normalisation des données avec design moderne
        const companies = [
          'MINISTÈRE DE L\'AGRICULTURE',
          'SENELEC',
          'ORANGE SÉNÉGAL',
          'SONATEL',
          'ECOBANK',
          'CBAO',
          'TOTAL SÉNÉGAL',
          'SOCOCIM',
          'DANGOTE',
          'EXPAT DAKAR'
        ];
        
        const jobTypes = ['CDI', 'CDD', 'Stage', 'Freelance', 'Temps partiel'];
        const salaries = ['300 000 - 500 000 FCFA', '500 000 - 800 000 FCFA', '800 000 - 1 200 000 FCFA', 'À négocier'];
        
        const mapped = items.slice(0, maxItems).map((it, index) => {
          // Trouver la ville correspondante par city_id
          const cityData = cities.find(city => city.id === it.city_id) || cities[index % cities.length];
          
          // Trouver la catégorie correspondante par category_id
          const categoryData = categories.find(cat => cat.id === it.category_id) || categories[index % categories.length];
          
          return {
            id: it.id || it.reference || Math.random().toString(36).slice(2),
            org: it.company_name || companies[index % companies.length],
            title: it.title || `Poste ${index + 1}`,
            category: categoryData?.name || 'Informatique & Technologie',
            location: cityData?.name || 'Dakar',
            type: it.post_type || jobTypes[index % jobTypes.length],
            date: it.created_at_formatted || 'Il y a 2 jours',
            excerpt: it.excerpt || 'Description de l\'Appel d\'offres avec les principales responsabilités et exigences du poste.',
            logoUrl: (it.logo_url?.small || it.logo_url?.full || it.logo || '').replace('https://localhost', 'http://localhost'),
            applications: it.applications_count || Math.floor(Math.random() * 50) + 1,
            views: it.visits || Math.floor(Math.random() * 200) + 50,
            viewsFormatted: it.visits_formatted || `${it.visits || Math.floor(Math.random() * 200) + 50} vues`,
            salary: it.salary_formatted || salaries[index % salaries.length],
            salaryMin: it.salary_min,
            salaryMax: it.salary_max,
            currencyCode: it.currency_code || 'XOF',
            negotiable: it.negotiable,
            isUrgent: it.featured || Math.random() > 0.8,
            isFeatured: it.featured || Math.random() > 0.9,
            experience: `${Math.floor(Math.random() * 5) + 1}-${Math.floor(Math.random() * 3) + 3} ans`,
            url: it.url,
            slug: it.slug,
            reference: it.reference,
            contactName: it.contact_name,
            email: it.email,
            phone: it.phone_national || it.phone,
            address: it.address,
            startDate: it.start_date,
            applicationUrl: it.application_url,
            tags: it.tags || [],
            description: it.description,
            companyDescription: it.company_description,
            lat: it.lat,
            lon: it.lon,
            createdAt: it.created_at,
            updatedAt: it.updated_at
          };
        });
        
        if (mounted) {
          setOffers(mapped);
          setTotalOffers(data?.total || mapped.length);
          setTotalPages(Math.ceil((data?.total || mapped.length) / maxItems));
        }
      } catch (e) {
        console.error('[ModernOffersList] fetch error:', e);
        const msg = e?.message || 'Erreur réseau';
        if (mounted) setError(`Impossible de charger les offres: ${msg}`);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchOffers();
    return () => { mounted = false; };
  }, [apiEndpoint, maxItems, currentPage, cities, categories]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSaveOffer = async (offer) => {
    try {
      console.log('Toggle sauvegarde de l\'offre:', offer);
      
      // Récupérer le token d'authentification
      const authHeaders = getAuthHeader();
      
      if (!authHeaders.Authorization) {
        alert('Vous devez être connecté pour sauvegarder une offre');
        return;
      }
      
      const isCurrentlySaved = savedPosts.includes(offer.id);
      const baseUrl = apiEndpoint.replace('/posts', '');
      
      if (isCurrentlySaved) {
        // Retirer des favoris - DELETE /savedPosts/{post_id}
        const deleteUrl = `${baseUrl}/savedPosts/${offer.id}`;
        
        const response = await fetch(deleteUrl, {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json',
            ...authHeaders
          }
        });

        if (response.ok) {
          setSavedPosts(prev => prev.filter(id => id !== offer.id));
          showToast('Offre retirée des favoris', 'info');
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('Erreur lors du retrait:', response.status, errorData);
          
          if (response.status === 401) {
            alert('Session expirée. Veuillez vous reconnecter.');
          } else {
            alert('Erreur lors du retrait de l\'offre des favoris.');
          }
        }
      } else {
        // Ajouter aux favoris - POST /savedPosts avec {"post_id": id}
        const saveUrl = `${baseUrl}/savedPosts`;
        
        const response = await fetch(saveUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...authHeaders
          },
          body: JSON.stringify({
            post_id: offer.id
          })
        });

        if (response.ok) {
          setSavedPosts(prev => [...prev, offer.id]);
          showToast('Offre ajoutée aux favoris !', 'success');
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('Erreur lors de la sauvegarde:', response.status, errorData);
          
          if (response.status === 401) {
            alert('Session expirée. Veuillez vous reconnecter.');
          } else if (response.status === 409) {
            // Déjà sauvegardé
            setSavedPosts(prev => [...prev, offer.id]);
            showToast('Offre déjà dans les favoris', 'info');
          } else {
            alert('Erreur lors de la sauvegarde de l\'offre.');
          }
        }
      }
    } catch (error) {
      console.error('Erreur réseau lors de la sauvegarde:', error);
      alert('Erreur de connexion. Veuillez vérifier votre connexion internet.');
    }
  };

  const handleApplyOffer = (offer) => {
    // Naviguer vers la page de détails de l'offre
    const id = offer?.id || offer?.slug || offer?.reference;
    if (id) {
      navigate(`${detailRouteBase}?id=${encodeURIComponent(id)}`);
    }
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      location: '',
      sector: '',
      salary: '',
      experience: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const filteredOffers = offers.filter(offer => {
    const matchesSearch = offer.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         offer.org.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         offer.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filters.type || offer.type === filters.type;
    const matchesLocation = !filters.location || offer.location === filters.location;
    const matchesSector = !filters.sector || offer.category.includes(filters.sector);
    
    return matchesSearch && matchesType && matchesLocation && matchesSector;
  });

  return (
    <div className="modern-offers-container">
      {/* Hero Section */}
      <div className="offers-hero">
        <div className="hero-background">
          <div className="hero-pattern"></div>
          <div className="hero-gradient"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge">
            <i className="fa fa-briefcase me-2"></i>
            Opportunités d'Appels d'offres
          </div>
          <h1 className="hero-title">
            Décrochez l'opportunité <span className="gradient-text">qui vous correspond</span>
            <div className="title-decoration"></div>
          </h1>
          <p className="hero-subtitle">
            Découvrez des milliers d'appels d'offres publics et privés dans tous les secteurs du Sénégal
          </p>
          
          {/* Barre de recherche moderne */}
          <div className="hero-search">
            <div className="search-container">
              <div className="search-field">
                <i className="fa fa-search"></i>
                <input 
                  type="text" 
                  placeholder="Titre du poste, mots-clés..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="search-field">
                <i className="fa fa-map-marker-alt"></i>
                <select 
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  disabled={loadingCities}
                >
                  <option value="">
                    {loadingCities ? 'Chargement des villes...' : 'Toutes les villes'}
                  </option>
                  {cities.map(city => (
                    <option key={city.id} value={city.name}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="search-field">
                <i className="fa fa-briefcase"></i>
                <select 
                  value={filters.sector}
                  onChange={(e) => handleFilterChange('sector', e.target.value)}
                  disabled={loadingCategories}
                >
                  <option value="">
                    {loadingCategories ? 'Chargement des secteurs...' : 'Tous les secteurs'}
                  </option>
                  {categories.map(category => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <button className="search-btn">
                <i className="fa fa-search"></i>
                Rechercher
              </button>
            </div>
          </div>

          {/* Stats rapides */}
          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-number">{totalOffers}+</span>
              <span className="stat-label">Appels d'offres</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">150+</span>
              <span className="stat-label">Entreprises</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">25+</span>
              <span className="stat-label">Secteurs d'activité</span>
            </div>
          </div>
        </div>
      </div>

      {/* Section principale */}
      <div className="offers-main-content">
        {/* Header de section */}
        <div className="section-header">
          <div className="section-left">
            <div className="offers-count-badge">
              <i className="fa fa-briefcase me-2"></i>
              <span>{filteredOffers.length} offre{filteredOffers.length > 1 ? 's' : ''} trouvée{filteredOffers.length > 1 ? 's' : ''}</span>
            </div>
            <h2 className="section-title">
              Toutes les offres
              <div className="title-underline"></div>
            </h2>
          </div>
          
          <div className="section-right">
            <div className="view-controls">
              <button 
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
              >
                <i className="fa fa-th-large"></i>
              </button>
              <button 
                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
              >
                <i className="fa fa-list"></i>
              </button>
            </div>
            
            <select 
              className="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="recent">Plus récentes</option>
              <option value="relevant">Plus pertinentes</option>
              <option value="salary">Salaire</option>
              <option value="company">Entreprise</option>
            </select>
          </div>
        </div>

        {/* Filtres avancés */}
        {showFilters && (
          <div className="filters-section">
            <div className="filters-header">
              <h6>Filtres</h6>
              <button className="clear-filters-btn" onClick={clearFilters}>
                <i className="fa fa-times me-1"></i>
                Effacer tout
              </button>
            </div>
            
            <div className="filters-grid">
              <div className="filter-group">
                <label>Type d'offre</label>
                <div className="filter-chips">
                  {['CDI', 'CDD', 'Stage', 'Freelance'].map(type => (
                    <button 
                      key={type}
                      className={`filter-chip ${filters.type === type ? 'active' : ''}`}
                      onClick={() => handleFilterChange('type', filters.type === type ? '' : type)}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="filter-group">
                <label>Expérience</label>
                <select 
                  value={filters.experience}
                  onChange={(e) => handleFilterChange('experience', e.target.value)}
                >
                  <option value="">Toute expérience</option>
                  <option value="0-1">0-1 an</option>
                  <option value="1-3">1-3 ans</option>
                  <option value="3-5">3-5 ans</option>
                  <option value="5+">5+ ans</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>Secteur</label>
                <select 
                  value={filters.sector}
                  onChange={(e) => handleFilterChange('sector', e.target.value)}
                  disabled={loadingCategories}
                >
                  <option value="">
                    {loadingCategories ? 'Chargement...' : 'Tous les secteurs'}
                  </option>
                  {categories.map(category => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <label>Salaire</label>
                <select 
                  value={filters.salary}
                  onChange={(e) => handleFilterChange('salary', e.target.value)}
                >
                  <option value="">Tous les salaires</option>
                  <option value="300-500">300 000 - 500 000 FCFA</option>
                  <option value="500-800">500 000 - 800 000 FCFA</option>
                  <option value="800+">800 000+ FCFA</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Liste des offres */}
        <div className={`offers-grid ${viewMode}`}>
          {loading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Chargement des offres...</p>
            </div>
          )}
          
          {error && !loading && (
            <div className="error-state">
              <i className="fa fa-exclamation-triangle"></i>
              <p>{error}</p>
              <button onClick={() => window.location.reload()}>Réessayer</button>
            </div>
          )}
          
          {!loading && !error && filteredOffers.length === 0 && (
            <div className="empty-state">
              <i className="fa fa-search"></i>
              <h3>Aucune offre trouvée</h3>
              <p>Essayez de modifier vos critères de recherche</p>
              <button onClick={clearFilters}>Effacer les filtres</button>
            </div>
          )}
          
          {!loading && !error && filteredOffers.map((offer) => (
            <ModernOfferCard 
              key={offer.id} 
              offer={offer}
              onSave={handleSaveOffer}
              onApply={handleApplyOffer}
              isSaved={savedPosts.includes(offer.id)}
            />
          ))}
        </div>

        {/* Pagination moderne améliorée */}
        {showPagination && filteredOffers.length > 0 && (
          <div className="pagination-container">
            {/* Informations de pagination */}
            <div className="pagination-info">
              <p className="pagination-text">
                Affichage de <strong>{((currentPage - 1) * maxItems) + 1}</strong> à{' '}
                <strong>{Math.min(currentPage * maxItems, totalOffers)}</strong> sur{' '}
                <strong>{totalOffers}</strong> offres
              </p>
              
              {/* Sélecteur d'éléments par page */}
              <div className="items-per-page">
                <label>Afficher :</label>
                <select 
                  value={maxItems} 
                  onChange={(e) => {
                    const newMaxItems = parseInt(e.target.value);
                    setCurrentPage(1);
                    // Ici vous pourriez ajouter la logique pour changer maxItems
                  }}
                  className="items-select"
                >
                  <option value="6">6 par page</option>
                  <option value="12">12 par page</option>
                  <option value="24">24 par page</option>
                  <option value="48">48 par page</option>
                </select>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="modern-pagination">
                <button 
                  className="pagination-btn prev"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="fa fa-chevron-left"></i>
                  Précédent
                </button>
                
                <div className="pagination-numbers">
                  {/* Première page */}
                  {currentPage > 3 && (
                    <>
                      <button 
                        className="pagination-number"
                        onClick={() => handlePageChange(1)}
                      >
                        1
                      </button>
                      {currentPage > 4 && <span className="pagination-dots">...</span>}
                    </>
                  )}
                  
                  {/* Pages autour de la page courante */}
                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1;
                    if (page >= currentPage - 2 && page <= currentPage + 2) {
                      return (
                        <button 
                          key={page}
                          className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      );
                    }
                    return null;
                  })}
                  
                  {/* Dernière page */}
                  {currentPage < totalPages - 2 && (
                    <>
                      {currentPage < totalPages - 3 && <span className="pagination-dots">...</span>}
                      <button 
                        className="pagination-number"
                        onClick={() => handlePageChange(totalPages)}
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </div>
                
                <button 
                  className="pagination-btn next"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Suivant
                  <i className="fa fa-chevron-right"></i>
                </button>
              </div>
            )}

            {/* Navigation rapide */}
            <div className="quick-navigation">
              <div className="page-jump">
                <label>Aller à la page :</label>
                <input 
                  type="number" 
                  min="1" 
                  max={totalPages}
                  className="page-input"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const page = parseInt(e.target.value);
                      if (page >= 1 && page <= totalPages) {
                        handlePageChange(page);
                        e.target.value = '';
                      }
                    }
                  }}
                  placeholder={currentPage}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Container pour les toasts */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}
